/**
 * Search & Filter Components
 *
 * Reusable components for marketplace search and filtering.
 */

import React, { useState } from 'react';
import {
  View,
  Text,
  Pressable,
  TextInput,
  ScrollView,
  Modal,
} from 'react-native';
import { noOutlineStyle } from '@/lib/platform';
import Animated, { FadeIn, SlideInRight } from 'react-native-reanimated';
import {
  Search,
  SlidersHorizontal,
  X,
  Check,
  ChevronDown,
  RotateCcw,
  MapPin,
} from 'lucide-react-native';
import { useThemeColors } from '@/lib/useThemeColors';
import { KeyboardAwareBody } from '@/components/modal/KeyboardAwareBody';
import { isWeb } from '@/lib/platform';
import { useResponsive } from '@/lib/useResponsive';
import {
  SearchFilters,
  INDUSTRIES,
  STAGES,
  STAGE_LABELS,
} from '@/lib/state/marketplace';
import { LocationAutocomplete } from '@/components/LocationAutocomplete';
import AnimatedPressable from '@/components/AnimatedPressable';

// ============================================
// Search Bar
// ============================================

interface SearchBarProps {
  value: string;
  onChangeText: (text: string) => void;
  placeholder?: string;
  onFilterPress?: () => void;
  activeFilterCount?: number;
}

export function SearchBar({
  value,
  onChangeText,
  placeholder = 'Search...',
  onFilterPress,
  activeFilterCount = 0,
}: SearchBarProps) {
  const colors = useThemeColors();
  return (
    <View className="flex-row items-center gap-2">
      <View
        className="flex-1 flex-row items-center gap-2 px-4 rounded-xl"
        style={{
          backgroundColor: colors.surface.primary,
          height: 48,
          borderWidth: 1,
          borderColor: colors.border.light,
        }}
      >
        <Search size={20} color={colors.text.tertiary} />
        <TextInput
          value={value}
          onChangeText={onChangeText}
          placeholder={placeholder}
          placeholderTextColor={colors.text.tertiary}
          className="flex-1 text-base"
          style={noOutlineStyle({ color: colors.text.primary })}
        />
        {value.length > 0 && (
          <Pressable onPress={() => onChangeText('')} className="p-1">
            <X size={18} color={colors.text.tertiary} />
          </Pressable>
        )}
      </View>

      {onFilterPress && (
        <AnimatedPressable
          onPress={onFilterPress}
          className="w-12 h-12 rounded-xl items-center justify-center relative"
          style={{
            backgroundColor: activeFilterCount > 0 ? colors.primary[50] : colors.surface.primary,
            borderWidth: 1,
            borderColor: activeFilterCount > 0 ? colors.primary[200] : colors.border.light,
          }}
        >
          <SlidersHorizontal
            size={20}
            color={activeFilterCount > 0 ? colors.primary[600] : colors.text.secondary}
          />
          {activeFilterCount > 0 && (
            <View
              className="absolute -top-1 -right-1 w-5 h-5 rounded-full items-center justify-center"
              style={{ backgroundColor: colors.primary[600] }}
            >
              <Text className="text-xs font-bold text-white">{activeFilterCount}</Text>
            </View>
          )}
        </AnimatedPressable>
      )}
    </View>
  );
}

// ============================================
// Filter Chips (Quick Filters)
// ============================================

interface FilterChipsProps {
  filters: SearchFilters;
  onToggle: (key: keyof SearchFilters, value: string) => void;
  onReset: () => void;
}

export function FilterChips({ filters, onToggle, onReset }: FilterChipsProps) {
  const colors = useThemeColors();
  const hasActiveFilters =
    filters.industries.length > 0 ||
    filters.stages.length > 0 ||
    filters.locations.length > 0 ||
    filters.status.length > 0;

  if (!hasActiveFilters) return null;

  return (
    <ScrollView
      horizontal
      showsHorizontalScrollIndicator={false}
      style={{ flexGrow: 0 }}
      className="mb-4"
    >
      <View className="flex-row gap-2 px-1">
        {filters.industries.map(industry => (
          <AnimatedPressable
            key={`industry-${industry}`}
            onPress={() => onToggle('industries', industry)}
            className="flex-row items-center gap-1 px-3 py-1.5 rounded-full"
            style={{ backgroundColor: colors.primary[100] }}
          >
            <Text style={{ color: colors.primary[700] }} className="text-sm">{industry}</Text>
            <X size={14} color={colors.primary[700]} />
          </AnimatedPressable>
        ))}

        {filters.stages.map(stage => (
          <AnimatedPressable
            key={`stage-${stage}`}
            onPress={() => onToggle('stages', stage)}
            className="flex-row items-center gap-1 px-3 py-1.5 rounded-full"
            style={{ backgroundColor: colors.traffic.greenLight }}
          >
            <Text style={{ color: colors.success }} className="text-sm">
              {STAGE_LABELS[stage] || stage}
            </Text>
            <X size={14} color={colors.success} />
          </AnimatedPressable>
        ))}

        {filters.locations.map(location => (
          <AnimatedPressable
            key={`location-${location}`}
            onPress={() => onToggle('locations', location)}
            className="flex-row items-center gap-1 px-3 py-1.5 rounded-full"
            style={{ backgroundColor: `${colors.primary[600]}15` }}
          >
            <MapPin size={13} color={colors.primary[600]} />
            <Text style={{ color: colors.primary[600] }} className="text-sm">
              {location.split(',')[0]}
            </Text>
            <X size={14} color={colors.primary[600]} />
          </AnimatedPressable>
        ))}

        {filters.status.map(status => (
          <AnimatedPressable
            key={`status-${status}`}
            onPress={() => onToggle('status', status)}
            className="flex-row items-center gap-1 px-3 py-1.5 rounded-full"
            style={{ backgroundColor: colors.amber.light }}
          >
            <Text style={{ color: colors.warning }} className="text-sm capitalize">{status}</Text>
            <X size={14} color={colors.warning} />
          </AnimatedPressable>
        ))}

        {hasActiveFilters && (
          <AnimatedPressable
            onPress={onReset}
            className="flex-row items-center gap-1 px-3 py-1.5 rounded-full"
            style={{ backgroundColor: colors.surface.secondary }}
          >
            <RotateCcw size={14} color={colors.text.secondary} />
            <Text style={{ color: colors.text.secondary }} className="text-sm">Clear all</Text>
          </AnimatedPressable>
        )}
      </View>
    </ScrollView>
  );
}

// ============================================
// Filter Section
// ============================================

interface FilterSectionProps {
  title: string;
  children: React.ReactNode;
}

function FilterSection({ title, children }: FilterSectionProps) {
  const colors = useThemeColors();
  return (
    <View className="mb-3">
      <Text
        style={{ color: colors.text.tertiary, letterSpacing: 0.8 }}
        className="text-xs font-medium uppercase mb-1.5"
      >
        {title}
      </Text>
      {children}
    </View>
  );
}

// ============================================
// Multi-Select Filter
// ============================================

interface MultiSelectProps {
  options: { value: string; label: string }[];
  selected: string[];
  onToggle: (value: string) => void;
  columns?: number;
}

function MultiSelect({ options, selected, onToggle, columns = 2 }: MultiSelectProps) {
  const colors = useThemeColors();
  return (
    <View className="flex-row flex-wrap gap-2">
      {options.map(option => {
        const isSelected = selected.includes(option.value);
        return (
          <AnimatedPressable
            key={option.value}
            onPress={() => onToggle(option.value)}
            className="flex-row items-center gap-2 px-3 py-2 rounded-lg"
            style={{
              backgroundColor: isSelected ? colors.primary[50] : colors.surface.secondary,
              borderWidth: 1,
              borderColor: isSelected ? colors.primary[200] : colors.border.light,
              minWidth: columns === 2 ? '48%' : columns === 3 ? '31%' : 'auto',
            }}
          >
            <View
              className="w-5 h-5 rounded items-center justify-center"
              style={{
                backgroundColor: isSelected ? colors.primary[600] : 'transparent',
                borderWidth: isSelected ? 0 : 1,
                borderColor: colors.border.light,
              }}
            >
              {isSelected && <Check size={14} color="white" />}
            </View>
            <Text
              style={{ color: isSelected ? colors.primary[700] : colors.text.secondary }}
              className="text-sm flex-1"
            >
              {option.label}
            </Text>
          </AnimatedPressable>
        );
      })}
    </View>
  );
}

// ============================================
// Pill Select (Wrap-flow pills for Industry / Location)
// ============================================

interface PillSelectProps {
  options: { value: string; label: string }[];
  selected: string[];
  onToggle: (value: string) => void;
}

function PillSelect({ options, selected, onToggle }: PillSelectProps) {
  const colors = useThemeColors();
  return (
    <View className="flex-row flex-wrap" style={{ gap: 6 }}>
      {options.map(option => {
        const isSelected = selected.includes(option.value);
        return (
          <Pressable
            key={option.value}
            onPress={() => onToggle(option.value)}
            style={{
              paddingHorizontal: 10,
              paddingVertical: 6,
              borderRadius: 999,
              backgroundColor: isSelected ? colors.primary[50] : colors.surface.secondary,
              borderWidth: 1,
              borderColor: isSelected ? colors.primary[200] : 'transparent',
            }}
          >
            <Text
              style={{ color: isSelected ? colors.primary[700] : colors.text.secondary }}
              className="text-xs font-medium"
            >
              {option.label}
            </Text>
          </Pressable>
        );
      })}
    </View>
  );
}

// ============================================
// Pill Toggle Row (Single-row for Stage / Status)
// ============================================

interface PillToggleRowProps {
  options: { value: string; label: string }[];
  selected: string[];
  onToggle: (value: string) => void;
}

function PillToggleRow({ options, selected, onToggle }: PillToggleRowProps) {
  const colors = useThemeColors();
  return (
    <View className="flex-row" style={{ gap: 6 }}>
      {options.map(option => {
        const isSelected = selected.includes(option.value);
        return (
          <Pressable
            key={option.value}
            onPress={() => onToggle(option.value)}
            className="flex-1 items-center rounded-lg"
            style={{
              paddingVertical: 8,
              backgroundColor: isSelected ? colors.primary[50] : colors.surface.secondary,
              borderWidth: 1,
              borderColor: isSelected ? colors.primary[200] : 'transparent',
            }}
          >
            <Text
              style={{ color: isSelected ? colors.primary[700] : colors.text.secondary }}
              className="text-xs font-medium text-center"
            >
              {option.label}
            </Text>
          </Pressable>
        );
      })}
    </View>
  );
}

// ============================================
// Preset Range Select (replaces RangeInput)
// ============================================

interface RangePreset {
  label: string;
  min: number;
  max: number;
}

interface PresetRangeSelectProps {
  presets: RangePreset[];
  value: { min: number; max: number };
  defaultRange: { min: number; max: number };
  onChange: (value: { min: number; max: number }) => void;
}

function PresetRangeSelect({ presets, value, defaultRange, onChange }: PresetRangeSelectProps) {
  const colors = useThemeColors();

  const isPresetActive = (preset: RangePreset) =>
    value.min === preset.min && value.max === preset.max;

  const handlePress = (preset: RangePreset) => {
    if (isPresetActive(preset)) {
      // Tapping active preset resets to default ("Any")
      onChange(defaultRange);
    } else {
      onChange({ min: preset.min, max: preset.max });
    }
  };

  return (
    <View className="flex-row" style={{ gap: 6 }}>
      {presets.map(preset => {
        const active = isPresetActive(preset);
        return (
          <Pressable
            key={preset.label}
            onPress={() => handlePress(preset)}
            className="flex-1 items-center rounded-lg"
            style={{
              paddingVertical: 8,
              backgroundColor: active ? colors.primary[50] : colors.surface.secondary,
              borderWidth: 1,
              borderColor: active ? colors.primary[200] : 'transparent',
            }}
          >
            <Text
              style={{ color: active ? colors.primary[700] : colors.text.secondary }}
              className="text-xs font-medium text-center"
            >
              {preset.label}
            </Text>
          </Pressable>
        );
      })}
    </View>
  );
}

// ============================================
// Range Slider (Simplified for RN)
// ============================================

interface RangeInputProps {
  label: string;
  min: number;
  max: number;
  value: { min: number; max: number };
  onChange: (value: { min: number; max: number }) => void;
  formatValue?: (value: number) => string;
  step?: number;
}

function RangeInput({
  label,
  min,
  max,
  value,
  onChange,
  formatValue = (v) => v.toString(),
  step = 1,
}: RangeInputProps) {
  const colors = useThemeColors();
  return (
    <View>
      <Text style={{ color: colors.text.secondary }} className="text-sm mb-2">
        {label}: {formatValue(value.min)} - {formatValue(value.max)}
      </Text>
      <View className="flex-row gap-2">
        <View
          className="flex-1 px-3 py-2 rounded-lg"
          style={{ backgroundColor: colors.surface.secondary }}
        >
          <Text style={{ color: colors.text.tertiary }} className="text-xs mb-1">Min</Text>
          <TextInput
            value={value.min.toString()}
            onChangeText={(text) => {
              const num = parseInt(text) || min;
              onChange({ ...value, min: Math.max(min, Math.min(num, value.max)) });
            }}
            keyboardType="numeric"
            style={{ color: colors.text.primary }}
            className="text-base"
          />
        </View>
        <View
          className="flex-1 px-3 py-2 rounded-lg"
          style={{ backgroundColor: colors.surface.secondary }}
        >
          <Text style={{ color: colors.text.tertiary }} className="text-xs mb-1">Max</Text>
          <TextInput
            value={value.max.toString()}
            onChangeText={(text) => {
              const num = parseInt(text) || max;
              onChange({ ...value, max: Math.min(max, Math.max(num, value.min)) });
            }}
            keyboardType="numeric"
            style={{ color: colors.text.primary }}
            className="text-base"
          />
        </View>
      </View>
    </View>
  );
}

// ============================================
// Full Filter Panel (Modal)
// ============================================

interface FilterPanelProps {
  visible: boolean;
  onClose: () => void;
  filters: SearchFilters;
  onUpdateFilter: <K extends keyof SearchFilters>(key: K, value: SearchFilters[K]) => void;
  onToggleFilter: (key: keyof SearchFilters, value: string) => void;
  onReset: () => void;
  showStatus?: boolean;
}

export function FilterPanel({
  visible,
  onClose,
  filters,
  onUpdateFilter,
  onToggleFilter,
  onReset,
  showStatus = true,
}: FilterPanelProps) {
  const colors = useThemeColors();
  const { isDesktop } = useResponsive();

  const industryOptions = INDUSTRIES.map(i => ({ value: i, label: i }));
  const stageOptions = STAGES.map(s => ({ value: s, label: STAGE_LABELS[s] || s }));
  const statusOptions = [
    { value: 'screening', label: 'Screening' },
    { value: 'interested', label: 'Interested' },
    { value: 'awaiting_access', label: 'Awaiting Access' },
    { value: 'diligence', label: 'In Diligence' },
    { value: 'rejected', label: 'Rejected' },
    { value: 'monitoring', label: 'Monitoring' },
  ];

  const scorePresets: RangePreset[] = [
    { label: 'Any', min: 0, max: 100 },
    { label: '60+', min: 60, max: 100 },
    { label: '70+', min: 70, max: 100 },
    { label: '80+', min: 80, max: 100 },
    { label: '90+', min: 90, max: 100 },
  ];

  const fundingPresets: RangePreset[] = [
    { label: 'Any', min: 0, max: 50000000 },
    { label: '<$500K', min: 0, max: 500000 },
    { label: '$500K-2M', min: 500000, max: 2000000 },
    { label: '$2M-10M', min: 2000000, max: 10000000 },
    { label: '$10M+', min: 10000000, max: 50000000 },
  ];

  const content = (
    <View className="flex-1" style={{ backgroundColor: colors.surface.primary }}>
      {/* Header */}
      <View
        className="flex-row items-center justify-between px-5 py-4 border-b"
        style={{ borderColor: colors.border.light }}
      >
        <Text style={{ color: colors.text.primary }} className="text-lg font-bold">
          Filters
        </Text>
        <View className="flex-row items-center gap-3">
          <Pressable onPress={onReset} className="px-3 py-1.5">
            <Text style={{ color: colors.text.secondary }} className="text-sm">Reset</Text>
          </Pressable>
          <Pressable
            onPress={onClose}
            className="w-8 h-8 rounded-full items-center justify-center"
            style={{ backgroundColor: colors.surface.secondary }}
          >
            <X size={18} color={colors.text.primary} />
          </Pressable>
        </View>
      </View>

      {/* Filter Content */}
      {/* Keyboard-aware: the range inputs sit near the bottom of this panel,
          so they were the fields most reliably hidden by the keyboard. */}
      <KeyboardAwareBody className="flex-1 px-5 py-4">
        <FilterSection title="Stage">
          <PillToggleRow
            options={stageOptions}
            selected={filters.stages}
            onToggle={(value) => onToggleFilter('stages', value)}
          />
        </FilterSection>

        <FilterSection title="Industry">
          <PillSelect
            options={industryOptions}
            selected={filters.industries}
            onToggle={(value) => onToggleFilter('industries', value)}
          />
        </FilterSection>

        <FilterSection title="Location">
          <LocationAutocomplete
            selectedLocations={filters.locations}
            onToggleLocation={(value) => onToggleFilter('locations', value)}
          />
        </FilterSection>

        {showStatus && (
          <FilterSection title="Status">
            <PillToggleRow
              options={statusOptions}
              selected={filters.status}
              onToggle={(value) => onToggleFilter('status', value)}
            />
          </FilterSection>
        )}

        <FilterSection title="Score Range">
          <PresetRangeSelect
            presets={scorePresets}
            value={filters.scoreRange}
            defaultRange={{ min: 0, max: 100 }}
            onChange={(value) => onUpdateFilter('scoreRange', value)}
          />
        </FilterSection>

        <FilterSection title="Funding Target">
          <PresetRangeSelect
            presets={fundingPresets}
            value={filters.fundingRange}
            defaultRange={{ min: 0, max: 50000000 }}
            onChange={(value) => onUpdateFilter('fundingRange', value)}
          />
        </FilterSection>

        <FilterSection title="Data Room">
          <View className="flex-row" style={{ gap: 6 }}>
            {[
              { value: null, label: 'Any' },
              { value: true, label: 'Has Room' },
              { value: false, label: 'No Room' },
            ].map((option) => {
              const active = filters.hasDataRoom === option.value;
              return (
                <Pressable
                  key={String(option.value)}
                  onPress={() => onUpdateFilter('hasDataRoom', option.value)}
                  className="flex-1 items-center rounded-lg"
                  style={{
                    paddingVertical: 8,
                    backgroundColor: active ? colors.primary[50] : colors.surface.secondary,
                    borderWidth: 1,
                    borderColor: active ? colors.primary[200] : 'transparent',
                  }}
                >
                  <Text
                    style={{ color: active ? colors.primary[700] : colors.text.secondary }}
                    className="text-xs font-medium text-center"
                  >
                    {option.label}
                  </Text>
                </Pressable>
              );
            })}
          </View>
        </FilterSection>

        <View className="h-4" />
      </KeyboardAwareBody>

      {/* Apply Button */}
      <View
        className="px-5 py-4 border-t"
        style={{ borderColor: colors.border.light }}
      >
        <AnimatedPressable
          onPress={onClose}
          className="py-4 rounded-xl items-center"
          style={{ backgroundColor: colors.primary[600] }}
        >
          <Text className="text-white font-semibold text-base">Apply Filters</Text>
        </AnimatedPressable>
      </View>
    </View>
  );

  if (isWeb && isDesktop) {
    // Slide-in panel for desktop
    if (!visible) return null;

    return (
      <Animated.View
        entering={SlideInRight.duration(300)}
        className="absolute right-0 top-0 bottom-0 z-50"
        style={{
          width: 380,
          shadowColor: '#000',
          shadowOffset: { width: -4, height: 0 },
          shadowOpacity: 0.15,
          shadowRadius: 20,
        }}
      >
        {content}
      </Animated.View>
    );
  }

  // Modal for mobile
  return (
    <Modal
      visible={visible}
      animationType="slide"
      presentationStyle="pageSheet"
      onRequestClose={onClose}
    >
      {content}
    </Modal>
  );
}

// ============================================
// Sort Dropdown
// ============================================

interface SortOption {
  value: string;
  label: string;
}

interface SortDropdownProps {
  value: string;
  onChange: (value: string) => void;
  options?: SortOption[];
}

export function SortDropdown({
  value,
  onChange,
  options = [
    { value: 'relevance', label: 'Relevance' },
    { value: 'match', label: 'Best Match' },
    { value: 'score', label: 'Highest Score' },
    { value: 'funding', label: 'Funding Amount' },
    { value: 'recent', label: 'Most Recent' },
  ],
}: SortDropdownProps) {
  const colors = useThemeColors();
  const [isOpen, setIsOpen] = useState(false);
  const selectedOption = options.find(o => o.value === value);

  return (
    <View className="relative">
      <Pressable
        onPress={() => setIsOpen(!isOpen)}
        className="flex-row items-center gap-2 px-3 py-2 rounded-lg"
        style={{
          backgroundColor: colors.surface.primary,
          borderWidth: 1,
          borderColor: colors.border.light,
        }}
      >
        <Text style={{ color: colors.text.secondary }} className="text-sm">
          Sort: {selectedOption?.label}
        </Text>
        <ChevronDown size={16} color={colors.text.tertiary} />
      </Pressable>

      {isOpen && (
        <Animated.View
          entering={FadeIn.duration(150)}
          className="absolute top-full left-0 right-0 mt-1 rounded-xl z-50 overflow-hidden"
          style={{
            backgroundColor: colors.surface.primary,
            borderWidth: 1,
            borderColor: colors.border.light,
            shadowColor: '#000',
            shadowOffset: { width: 0, height: 4 },
            shadowOpacity: 0.15,
            shadowRadius: 12,
            elevation: 5,
          }}
        >
          {options.map((option) => (
            <AnimatedPressable
              key={option.value}
              onPress={() => {
                onChange(option.value);
                setIsOpen(false);
              }}
              className="px-4 py-3 flex-row items-center justify-between"
              style={{
                backgroundColor: value === option.value ? colors.primary[50] : 'transparent',
              }}
            >
              <Text
                style={{
                  color: value === option.value ? colors.primary[700] : colors.text.primary,
                }}
                className="text-sm"
              >
                {option.label}
              </Text>
              {value === option.value && <Check size={16} color={colors.primary[600]} />}
            </AnimatedPressable>
          ))}
        </Animated.View>
      )}
    </View>
  );
}
